/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactNode, type FC } from 'react';

import { type GetBotInfo, type IMessage } from '@adp-common/chat-uikit-shared';
import { type TextMixItem } from '@adp-common/chat-core';

import { TextContent, type IMessageContentProps } from '../text-content';
import { PlainTextContent } from '../plain-text-content';
import { typeSafeMessageBoxInnerVariants } from '../../../variants/message-box-inner-variants';
import { isTextMixItem } from '../../../utils/multimodal';
import { projectMultimodalText } from '../../../utils/message-text';

export interface TextItemListProps
  extends Pick<
    IMessageContentProps,
    | 'mdBoxProps'
    | 'onLinkClick'
    | 'onImageClick'
    | 'enableAutoSizeImage'
    | 'readonly'
  > {
  textItemList: TextMixItem[];
  renderTextContentAddonTop: ReactNode;
  message: IMessage;
  showBackground: boolean;
  getBotInfo: GetBotInfo;
  isContentLoading: boolean | undefined;
}

export const TextItemList: FC<TextItemListProps> = ({
  textItemList,
  renderTextContentAddonTop,
  message,
  showBackground,
  getBotInfo,
  isContentLoading,
  mdBoxProps,
  ...textProps
}) => {
  if (message.role === 'assistant') {
    const projected = projectMultimodalText(message, mdBoxProps);
    if (!projected.message.content.trim()) {
      return null;
    }
    return (
      <>
        {renderTextContentAddonTop}
        <TextContent {...textProps} {...projected} />
      </>
    );
  }
  return (
    <>
      {textItemList.map((item, index) => {
        if (isTextMixItem(item)) {
          const TextContentAddonTop = renderTextContentAddonTop;
          const isTextAndMentionedEmpty =
            !item.text && !message.mention_list.at(0);

          if (isTextAndMentionedEmpty) {
            return null;
          }

          return (
            <div
              className={typeSafeMessageBoxInnerVariants({
                color: 'primary',
                border: null,
                tight: false,
                showBackground,
              })}
              style={{ width: 'fit-content' }}
              key={index}
            >
              {TextContentAddonTop}
              <PlainTextContent
                isContentLoading={isContentLoading}
                content={item.text}
                mentioned={message.mention_list.at(0)}
                getBotInfo={getBotInfo}
              />
            </div>
          );
        }
      })}
    </>
  );
};
